import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { photoUrl } from "@/lib/placeholders";
import { Loader2, ChevronRight, Activity } from "lucide-react";

// La finestra dopo un QR: «cosa vuoi fare?».
//
// Il tesserino, la targa, l'etichetta di magazzino e il cartello del cantiere
// portano ognuno a tre o quattro cose, e non alla stessa pagina per tutti:
// il preposto davanti a un mezzo segnala un'anomalia e guarda le scadenze,
// il tecnico registra il controllo. Le scelte si filtrano sul ruolo; se ne
// resta una sola si va dritti, senza finestra.
//
// «Verifica stato» non cambia pagina: apre qui dentro il semaforo che manda
// il server (/qr/{tipo}/{id}), gia' filtrato su cosa quel ruolo puo' vedere.
const TUTTI = ["admin", "amministrativo", "operator", "tecnico", "preposto"];
const GESTIONE = ["admin", "amministrativo", "tecnico"];
const MAGAZZINO = ["admin", "amministrativo", "operator", "tecnico"];

const SCELTE = {
  dipendente: [
    { nome: "Consegna DPI", spiega: "la consegna si apre con il nome già scelto",
      ruoli: MAGAZZINO, via: (id) => `/consegna?dipendente=${id}` },
    { nome: "Fascicolo", spiega: "documenti, idoneità, attestati",
      ruoli: GESTIONE, via: (id) => `/dipendenti?fascicolo=${id}` },
    { nome: "Verifica stato", spiega: "attestati e documenti: cosa è scaduto", stato: true,
      ruoli: ["admin", "amministrativo", "tecnico", "preposto"] },
    { nome: "Mostra tesserino", spiega: "il tesserino di riconoscimento, da far vedere",
      ruoli: TUTTI, via: (id) => `/fascicolo/${id}?tesserino=1` },
  ],
  mezzo: [
    { nome: "Registra controllo o intervento", spiega: "sul libro di manutenzione",
      ruoli: GESTIONE, via: (id) => `/attrezzature/${id}?azione=controllo` },
    { nome: "Segnala anomalia", spiega: "la segnalazione parte già collegata al mezzo",
      ruoli: TUTTI, via: (id, nome) => `/segnalazioni?mezzo=${id}&nome=${encodeURIComponent(nome || "")}` },
    { nome: "Verifica stato", spiega: "manutenzione, documenti, cassetta di primo soccorso", stato: true,
      ruoli: ["admin", "amministrativo", "tecnico", "preposto"] },
    { nome: "Scheda del mezzo", spiega: "documenti, storico, costi",
      ruoli: GESTIONE, via: (id) => `/attrezzature/${id}` },
  ],
  materiale: [
    { nome: "Prelievo", spiega: "scarico verso un cantiere o un mezzo",
      ruoli: ["admin", "amministrativo", "operator", "tecnico", "preposto"], via: (id) => `/movimenti-materiali?mat=${id}&tipo=scarico` },
    { nome: "Carico", spiega: "arrivo di materiale a magazzino",
      ruoli: MAGAZZINO, via: (id) => `/movimenti-materiali?mat=${id}&tipo=carico` },
    { nome: "Giacenza", spiega: "quanto ce n'è e la scorta minima", stato: true, ruoli: TUTTI },
  ],
  dpi: [
    { nome: "Consegna a un dipendente", spiega: "l'articolo è già nella consegna",
      ruoli: MAGAZZINO, via: (id) => `/consegna?articolo=${id}` },
    { nome: "Carico o rettifica giacenza", spiega: "la scheda dell'articolo a magazzino",
      ruoli: MAGAZZINO, via: (id) => `/magazzino?articolo=${id}` },
    { nome: "Giacenza per taglia", spiega: "quanti pezzi per ogni taglia", stato: true, ruoli: TUTTI },
  ],
  cantiere: [
    { nome: "Verifica in cantiere", spiega: "nuova verifica di sicurezza e ambiente, sul cantiere giusto",
      ruoli: ["admin", "amministrativo", "tecnico", "preposto"], via: (id) => `/cantieri/${id}?azione=verifica` },
    { nome: "Segnalazione dal cantiere", spiega: "near miss, anomalia, richiesta",
      ruoli: TUTTI, via: (id, nome) => `/segnalazioni?cantiere=${id}&nome=${encodeURIComponent(nome || "")}` },
    { nome: "Scheda cantiere", spiega: "squadra, POS, documenti",
      ruoli: ["admin", "amministrativo", "tecnico", "preposto"], via: (id) => `/cantieri/${id}` },
  ],
};

const COLORE = {
  scaduto: "bg-red-100 text-red-700", in_scadenza: "bg-amber-100 text-amber-700",
  ok: "bg-emerald-100 text-emerald-700", senza_scadenza: "bg-slate-100 text-slate-500",
};
const NOME_STATO = { scaduto: "scaduto", in_scadenza: "in scadenza", ok: "ok", senza_scadenza: "—" };

export function scelteDisponibili(tipo, ruolo) {
  return (SCELTE[tipo] || []).filter((s) => s.ruoli.includes(ruolo));
}

export default function SceltaQR({ target, user, onChiudi }) {
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [errore, setErrore] = useState(null);
  const [vediStato, setVediStato] = useState(false);
  const scelte = scelteDisponibili(target.tipo, user?.role);

  useEffect(() => {
    let vivo = true;
    api.get(`/qr/${target.tipo}/${target.id}`)
      .then(({ data }) => { if (vivo) setInfo(data); })
      .catch((e) => { if (vivo) setErrore(e.response?.data?.detail || "Non trovato"); });
    return () => { vivo = false; };
  }, [target.tipo, target.id]);

  // Una scelta sola e nessun semaforo da mostrare: si va e basta.
  useEffect(() => {
    if (scelte.length === 1 && !scelte[0].stato) {
      onChiudi();
      nav(scelte[0].via(target.id, target.label));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const vai = (s) => {
    if (s.stato) { setVediStato((v) => !v); return; }
    onChiudi();
    nav(s.via(target.id, info?.titolo || ""));
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onChiudi(); }}>
      <DialogContent className="max-w-md" data-testid="scelta-qr">
        <DialogHeader>
          <DialogTitle className="text-[11px] uppercase tracking-widest text-slate-400 font-medium">
            {target.label}
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-3">
          {info?.foto && (
            <img src={photoUrl(info.foto)} alt="" className="w-14 h-14 rounded-full object-cover border border-slate-200 flex-shrink-0" />
          )}
          <div className="min-w-0 flex-1">
            <div className="font-display text-xl font-black tracking-tight leading-tight">
              {info ? info.titolo : errore ? "Non trovato" : <Loader2 className="w-5 h-5 animate-spin text-slate-400" />}
            </div>
            {info?.sottotitolo && <div className="text-sm text-slate-500">{info.sottotitolo}</div>}
            {info?.riassunto && (
              <div className={`inline-block mt-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                /scadut|esaurit/i.test(info.riassunto) ? "bg-red-100 text-red-700"
                  : /scadenza|sotto scorta/i.test(info.riassunto) ? "bg-amber-100 text-amber-700"
                  : "bg-emerald-100 text-emerald-700"}`}>
                {info.riassunto}
              </div>
            )}
            {errore && <div className="text-sm text-red-600">{errore}</div>}
          </div>
        </div>

        {scelte.length === 0 && (
          <p className="text-sm text-slate-500">Con il tuo profilo qui non c&apos;è niente da fare.</p>
        )}

        <div className="space-y-2">
          {scelte.map((s) => (
            <button key={s.nome} type="button" onClick={() => vai(s)}
                    disabled={Boolean(errore) && !s.stato}
                    data-testid={`qr-scelta-${s.nome.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={`w-full text-left px-4 py-3 rounded-lg border flex items-center gap-3 transition-colors ${
                      s.stato && vediStato ? "border-[#0067b2] bg-blue-50/50" : "border-slate-200 hover:border-orange-300 hover:bg-orange-50/40"}`}>
              {s.stato ? <Activity className="w-4 h-4 text-[#0067b2] flex-shrink-0" />
                       : <ChevronRight className="w-4 h-4 text-orange-500 flex-shrink-0" />}
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-slate-800">{s.nome}</span>
                <span className="block text-xs text-slate-500">{s.spiega}</span>
              </span>
            </button>
          ))}
        </div>

        {vediStato && info && (
          <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 max-h-[40vh] overflow-y-auto">
            {info.voci.length === 0 && (
              <p className="text-sm text-slate-500 p-3">Niente da mostrare.</p>
            )}
            {info.voci.map((v, i) => (
              <div key={i} className="flex items-center gap-2 px-3 py-2">
                <span className="flex-1 min-w-0">
                  <span className="block text-sm truncate">{v.nome}</span>
                  {v.gruppo && v.gruppo !== v.nome && <span className="block text-[11px] text-slate-400">{v.gruppo}</span>}
                </span>
                <span className="text-xs text-slate-500 tabular-nums">{v.scadenza || v.valore || ""}</span>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${COLORE[v.stato] || COLORE.senza_scadenza}`}>
                  {NOME_STATO[v.stato] || v.stato}
                </span>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end">
          <Button variant="outline" onClick={onChiudi}>Chiudi</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
